<template>
  <transition name="fade-scale">
    <div
      v-if="visible"
      ref="tooltip"
      class="smart-tooltip"
      :class="[
        `tooltip-${tooltip.type}`,
        { 'dark-mode': isDarkMode }
      ]"
      :style="tooltipStyle"
      role="tooltip"
      @mouseenter="pauseAutoHide"
      @mouseleave="resumeAutoHide"
    >
      <!-- Close Button -->
      <button
        v-if="tooltip.is_dismissible !== false"
        class="tooltip-close"
        @click="dismiss"
        aria-label="Close tooltip"
      >
        <c-icon name="cilX" />
      </button>

      <!-- Tooltip Header -->
      <div class="tooltip-header">
        <c-icon :name="tooltip.icon || getDefaultIcon(tooltip.type)" class="tooltip-icon" />
        <h4 class="tooltip-title">{{ tooltip.title }}</h4>
      </div>

      <!-- Tooltip Content -->
      <div class="tooltip-content">
        <p v-if="tooltip.help_text" class="help-text">{{ tooltip.help_text }}</p>
        <p v-if="tooltip.content" class="content-text">{{ tooltip.content }}</p>
        
        <!-- Detailed Help (Expandable) -->
        <div v-if="tooltip.detailed_help" class="detailed-help">
          <button 
            class="expand-button" 
            @click="showDetails = !showDetails"
          >
            <c-icon :name="showDetails ? 'cilChevronTop' : 'cilChevronBottom'" />
            {{ showDetails ? 'Less Details' : 'More Details' }}
          </button>
          <transition name="slide-down">
            <div v-if="showDetails" class="detailed-content">
              {{ tooltip.detailed_help }}
            </div>
          </transition>
        </div>
      </div>

      <!-- Action Buttons -->
      <div v-if="tooltip.action_buttons && tooltip.action_buttons.length > 0" class="tooltip-actions">
        <button
          v-for="(action, index) in tooltip.action_buttons"
          :key="index"
          class="action-button"
          :class="`action-${action.type || 'default'}`"
          @click="handleAction(action)"
        >
          <c-icon v-if="action.icon" :name="action.icon" />
          {{ action.label }}
        </button>
      </div>

      <!-- Feedback Buttons -->
      <div class="tooltip-feedback">
        <span class="feedback-label">Was this helpful?</span>
        <div class="feedback-buttons">
          <button
            class="feedback-btn helpful"
            @click="markHelpful"
            :disabled="feedbackGiven"
            title="Yes, this was helpful"
          >
            <c-icon name="cilThumbUp" />
          </button>
          <button
            class="feedback-btn not-helpful"
            @click="markNotHelpful"
            :disabled="feedbackGiven"
            title="No, not helpful"
          >
            <c-icon name="cilThumbDown" />
          </button>
        </div>
      </div>

      <!-- Tooltip Arrow -->
      <div class="tooltip-arrow" :style="arrowStyle"></div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'SmartTooltip',
  
  props: {
    tooltip: {
      type: Object,
      required: true,
    },
    targetElement: {
      type: [Element, Object],
      default: null,
    },
    position: {
      type: String,
      default: 'auto', // auto, top, bottom, left, right
      validator: (value) => ['auto', 'top', 'bottom', 'left', 'right'].includes(value),
    },
    autoHide: {
      type: Boolean,
      default: false,
    },
    autoHideDelay: {
      type: Number,
      default: 10000, // 10 seconds
    },
  },
  
  data() {
    return {
      visible: false,
      showDetails: false,
      feedbackGiven: false,
      tooltipStyle: {},
      arrowStyle: {},
      autoHideTimer: null,
      displayStartTime: null,
    };
  },
  
  computed: {
    isDarkMode() {
      return this.$store.state.darkMode;
    },
  },
  
  mounted() {
    console.log('[SmartTooltip] Mounted with tooltip:', {
      id: this.tooltip.id,
      title: this.tooltip.title,
      type: this.tooltip.type,
      feature: this.tooltip.feature_area,
    });
    
    this.show();
    this.setupKeyboardListeners();
    
    if (this.autoHide) {
      this.startAutoHideTimer();
    }
  },
  
  beforeDestroy() {
    this.removeKeyboardListeners();
    this.clearAutoHideTimer();
  },
  
  methods: {
    /**
     * Show tooltip
     */
    show() {
      this.visible = true;
      this.displayStartTime = Date.now();
      
      console.log('[SmartTooltip] Showing tooltip:', this.tooltip.title);
      
      this.$nextTick(() => {
        this.calculatePosition();
        window.addEventListener('resize', this.calculatePosition);
        window.addEventListener('scroll', this.calculatePosition);
      });
    },
    
    /**
     * Hide tooltip
     */
    hide() {
      this.visible = false;
      window.removeEventListener('resize', this.calculatePosition);
      window.removeEventListener('scroll', this.calculatePosition);
      this.$emit('hide');
    },
    
    /**
     * Dismiss tooltip
     */
    dismiss() {
      const displayDuration = Date.now() - this.displayStartTime;
      
      this.$store.dispatch('proactiveHelp/hideTooltip', this.tooltip.id);
      this.hide();
      
      this.$emit('dismiss', {
        tooltipId: this.tooltip.id,
        displayDuration,
      });
    },
    
    /**
     * Calculate tooltip position relative to target element
     */
    calculatePosition() {
      if (!this.targetElement || !this.$refs.tooltip) {
        console.warn('[SmartTooltip] Missing target element or tooltip ref');
        return;
      }
      
      const target = this.targetElement.getBoundingClientRect();
      const tooltip = this.$refs.tooltip.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      
      let position = this.position;
      
      // Auto-detect best position
      if (position === 'auto') {
        position = this.detectBestPosition(target, tooltip, viewport);
      }
      
      const coords = this.calculateCoordinates(position, target, tooltip);
      
      this.tooltipStyle = {
        top: `${coords.top}px`,
        left: `${coords.left}px`,
      };
      
      this.arrowStyle = this.calculateArrowStyle(position);
    },
    
    /**
     * Detect best position for tooltip
     */
    detectBestPosition(target, tooltip, viewport) {
      const spaceTop = target.top;
      const spaceBottom = viewport.height - target.bottom;
      const spaceLeft = target.left;
      const spaceRight = viewport.width - target.right;
      
      // Prefer bottom if enough space
      if (spaceBottom >= tooltip.height + 20) return 'bottom';
      if (spaceTop >= tooltip.height + 20) return 'top';
      if (spaceRight >= tooltip.width + 20) return 'right';
      if (spaceLeft >= tooltip.width + 20) return 'left';
      
      // Fallback to bottom
      return 'bottom';
    },
    
    /**
     * Calculate tooltip coordinates based on position
     */
    calculateCoordinates(position, target, tooltip) {
      const offset = 12; // Distance from target element
      let top = 0;
      let left = 0;
      
      switch (position) {
        case 'top':
          top = target.top - tooltip.height - offset;
          left = target.left + (target.width / 2) - (tooltip.width / 2);
          break;
        
        case 'bottom':
          top = target.bottom + offset;
          left = target.left + (target.width / 2) - (tooltip.width / 2);
          break;
        
        case 'left':
          top = target.top + (target.height / 2) - (tooltip.height / 2);
          left = target.left - tooltip.width - offset;
          break;
        
        case 'right':
          top = target.top + (target.height / 2) - (tooltip.height / 2);
          left = target.right + offset;
          break;
      }
      
      // Keep tooltip within viewport
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      
      top = Math.max(10, Math.min(top, viewport.height - tooltip.height - 10));
      left = Math.max(10, Math.min(left, viewport.width - tooltip.width - 10));
      
      return { top, left };
    },
    
    /**
     * Calculate arrow style based on position
     */
    calculateArrowStyle(position) {
      const styles = {
        top: { bottom: '-8px', left: '50%', transform: 'translateX(-50%) rotate(45deg)' },
        bottom: { top: '-8px', left: '50%', transform: 'translateX(-50%) rotate(45deg)' },
        left: { right: '-8px', top: '50%', transform: 'translateY(-50%) rotate(45deg)' },
        right: { left: '-8px', top: '50%', transform: 'translateY(-50%) rotate(45deg)' },
      };
      
      return styles[position] || styles.bottom;
    },
    
    /**
     * Handle action button click
     */
    async handleAction(action) {
      const displayDuration = Date.now() - this.displayStartTime;
      
      // Dispatch to store
      await this.$store.dispatch('proactiveHelp/handleTooltipAction', {
        tooltipId: this.tooltip.id,
        action,
        buttonLabel: action.label,
      });
      
      this.$emit('action', {
        tooltipId: this.tooltip.id,
        action,
        displayDuration,
      });
      
      // Execute action if has URL
      if (action.url) {
        if (action.type === 'navigate') {
          this.$router.push(action.url);
        } else if (action.type === 'link') {
          window.open(action.url, '_blank');
        }
      }
      
      this.hide();
    },
    
    /**
     * Mark as helpful
     */
    async markHelpful() {
      this.feedbackGiven = true;
      
      await this.$store.dispatch('proactiveHelp/markHelpful', {
        tooltipId: this.tooltip.id,
        feedback: 'Helpful',
      });
      
      this.$emit('feedback', {
        tooltipId: this.tooltip.id,
        helpful: true,
      });
      
      setTimeout(() => this.hide(), 1000);
    },
    
    /**
     * Mark as not helpful
     */
    async markNotHelpful() {
      this.feedbackGiven = true;
      
      await this.$store.dispatch('proactiveHelp/markNotHelpful', {
        tooltipId: this.tooltip.id,
        feedback: 'Not helpful',
      });
      
      this.$emit('feedback', {
        tooltipId: this.tooltip.id,
        helpful: false,
      });
      
      setTimeout(() => this.hide(), 1000);
    },
    
    /**
     * Get default icon for tooltip type
     */
    getDefaultIcon(type) {
      const icons = {
        info: 'cilInfo',
        warning: 'cilWarning',
        suggestion: 'cilLightbulb',
        tutorial: 'cilBook',
        error_help: 'cilBug',
      };
      return icons[type] || 'cilInfo';
    },
    
    /**
     * Setup keyboard listeners
     */
    setupKeyboardListeners() {
      console.log('Setting up keyboard listeners')
      document.addEventListener('keydown', this.handleKeydown);
    },
    
    /**
     * Remove keyboard listeners
     */
    removeKeyboardListeners() {
      document.removeEventListener('keydown', this.handleKeydown);
    },
    
    /**
     * Handle keyboard events
     */
    handleKeydown(e) {
      console.log('Handling keydown event...')
      if (e.key === 'Escape') {
        this.dismiss();
      }
    },
    
    /**
     * Start auto-hide timer
     */
    startAutoHideTimer() {
      this.autoHideTimer = setTimeout(() => {
        this.dismiss();
      }, this.autoHideDelay);
    },
    
    /**
     * Pause auto-hide timer
     */
    pauseAutoHide() {
      if (this.autoHideTimer) {
        clearTimeout(this.autoHideTimer);
      }
    },
    
    /**
     * Resume auto-hide timer
     */
    resumeAutoHide() {
      if (this.autoHide) {
        this.startAutoHideTimer();
      }
    },
    
    /**
     * Clear auto-hide timer
     */
    clearAutoHideTimer() {
      if (this.autoHideTimer) {
        clearTimeout(this.autoHideTimer);
        this.autoHideTimer = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.smart-tooltip {
  position: fixed;
  max-width: 350px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  padding: 16px;
  z-index: 10000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  
  // Type-specific colors
  &.tooltip-info {
    border-left: 4px solid #3b82f6;
    .tooltip-icon { color: #3b82f6; }
  }
  
  &.tooltip-warning {
    border-left: 4px solid #f59e0b;
    .tooltip-icon { color: #f59e0b; }
  }
  
  &.tooltip-suggestion {
    border-left: 4px solid #8b5cf6;
    .tooltip-icon { color: #8b5cf6; }
  }
  
  &.tooltip-tutorial {
    border-left: 4px solid #10b981;
    .tooltip-icon { color: #10b981; }
  }
  
  &.tooltip-error_help {
    border-left: 4px solid #ef4444;
    .tooltip-icon { color: #ef4444; }
  }
  
  // Dark mode
  &.dark-mode {
    background: #2a2a2a;
    color: #e0e0e0;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    
    .tooltip-content {
      color: #e0e0e0;
    }
    
    .expand-button,
    .action-button {
      background: #3a3a3a;
      color: #e0e0e0;
      
      &:hover {
        background: #4a4a4a;
      }
    }
    
    .feedback-btn {
      background: #3a3a3a;
      color: #e0e0e0;
      
      &:hover:not(:disabled) {
        background: #4a4a4a;
      }
    }
  }
}

.tooltip-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 18px;
  padding: 4px;
  line-height: 1;
  transition: color 0.2s;
  
  &:hover {
    color: #6b7280;
  }
}

.tooltip-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  
  .tooltip-icon {
    font-size: 24px;
    flex-shrink: 0;
  }
  
  .tooltip-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    
    .dark-mode & {
      color: #f3f4f6;
    }
  }
}

.tooltip-content {
  margin-bottom: 12px;
  
  .help-text {
    margin: 0 0 8px 0;
    font-size: 14px;
    line-height: 1.5;
    color: #374151;
  }
  
  .content-text {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: #6b7280;
  }
}

.detailed-help {
  margin-top: 12px;
  
  .expand-button {
    background: #f3f4f6;
    border: none;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    color: #4b5563;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: background 0.2s;
    
    &:hover {
      background: #e5e7eb;
    }
    
    i {
      font-size: 10px;
    }
  }
  
  .detailed-content {
    margin-top: 8px;
    padding: 12px;
    background: #f9fafb;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1.5;
    color: #4b5563;
    
    .dark-mode & {
      background: #1f1f1f;
      color: #d1d5db;
    }
  }
}

.tooltip-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  
  .action-button {
    flex: 1;
    min-width: 120px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #ffffff;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: transform 0.2s, box-shadow 0.2s;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    i {
      font-size: 14px;
    }
  }
}

.tooltip-feedback {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  
  .dark-mode & {
    border-top-color: #3a3a3a;
  }
  
  .feedback-label {
    font-size: 12px;
    color: #6b7280;
  }
  
  .feedback-buttons {
    display: flex;
    gap: 8px;
  }
  
  .feedback-btn {
    background: #f3f4f6;
    border: none;
    padding: 6px 10px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
    
    &:hover:not(:disabled) {
      background: #e5e7eb;
      transform: scale(1.1);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    &.helpful {
      color: #10b981;
      
      &:hover:not(:disabled) {
        background: #d1fae5;
      }
    }
    
    &.not-helpful {
      color: #ef4444;
      
      &:hover:not(:disabled) {
        background: #fee2e2;
      }
    }
  }
}

.tooltip-arrow {
  position: absolute;
  width: 16px;
  height: 16px;
  background: #ffffff;
  
  .dark-mode & {
    background: #2a2a2a;
  }
}

/* Animations */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-scale-enter,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  max-height: 200px;
  overflow: hidden;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
